/**
 * Assistant Diagram recipe.
 *
 * The Ardoq AI Assistant no longer emits geometry — the frontend already holds
 * the full graph. Instead the LLM emits this small declarative recipe (which
 * layout, how to group, how to color), which `applyRecipe` applies to the
 * in-view graph and hands to `@ardoq/graph-view` (yFiles) to lay out and render.
 *
 * Output is O(rules), not O(nodes): generation is fast and bounded regardless of
 * graph size.
 *
 * This file is the single source of truth for the recipe contract: the diagram is
 * a frontend tool (`aiControlledCommands/viewpoint/diagram.ts`), so `typia.json.schema` derives
 * the LLM-facing JSON schema from these types and their JSDoc — keep the comments
 * accurate, they are sent to the model. `parseRecipe` then normalizes whatever the
 * tool result carries and is deliberately tolerant (fills defaults; never throws).
 */
import { isRecord } from '@ardoq/common-helpers';
import type { HierarchicalLayoutRoutingStyleStringValues } from '@ardoq/yfiles';
export type RecipeLayoutType = 'hierarchical' | 'organic' | 'circular' | 'orthogonal' | 'tree' | 'radial' | 'compact-disk';
export type RecipeOrientation = 'left-to-right' | 'top-to-bottom';
export type RecipeEdgeRouting = HierarchicalLayoutRoutingStyleStringValues;
/**
 * The layout choice plus only the options that layout actually supports — a tagged
 * union keyed by `type`. This keeps layout-specific options off layouts that ignore
 * them, and lets the contract grow (per-layout options) without a flat bag of
 * conditionally-meaningful fields.
 */
export type RecipeLayout = {
    /** Layered layout for dependency/flow/breakdown structures with a sense of direction. */
    type: 'hierarchical';
    /** Flow direction of the layered layout; pick what reads best. */
    orientation: RecipeOrientation;
    /**
     * How edges are routed: 'orthogonal' for right-angled connectors,
     * 'octilinear' for 45-degree diagonal segments, 'polyline' for direct
     * straight lines, 'curved' for smooth curves.
     */
    edgeRouting: RecipeEdgeRouting;
    /**
     * Group edges sharing a source or target into a common bus, reducing the
     * number of distinct edge entry/exit points per node. Keep on for most
     * graphs; turn off when individual edge paths must stay distinct.
     */
    groupEdges: boolean;
    } | {
    /** Force-directed layout for densely interconnected graphs with no clear direction. */
    type: 'organic';
    } | {
    /** Arranges nodes around circles — good for showing clusters and cycles. */
    type: 'circular';
    /** Bundle edges sharing a circle partition into tighter strands to cut clutter on dense graphs. */
    bundleEdges: boolean;
    } | {
    /** Schematic, grid-routed layout for network-diagram style structures. */
    type: 'orthogonal';
    } | {
    /** Strict hierarchy / breakdown-structure layout. */
    type: 'tree';
    } | {
    /** Arranges nodes on concentric circles around central nodes (distance from centre = depth). */
    type: 'radial';
    /** Bundle edges toward the centre into tighter strands to cut clutter on dense graphs. */
    bundleEdges: boolean;
    } | {
    /** Packs nodes tightly into a disk — good for large sets with few edges. */
    type: 'compact-disk';
};
/**
 * A small declarative recipe describing how to lay out and style the graph the
 * user is currently viewing in the Assistant Diagram. This is NOT geometry: it
 * carries no node positions or coordinates. The frontend already holds the
 * in-view graph and lays it out client-side (yFiles) by applying this recipe, so
 * the output stays tiny and fast regardless of graph size. When the request is
 * vague, default to a hierarchical layout with the legend on.
 */
export type AssistantDiagramRecipe = {
    /** A short, descriptive title for the diagram. */
    title: string;
    /**
     * The layout and the options that layout supports. Choose the layout that best
     * fits the data, then set its options: 'hierarchical' (set orientation +
     * edgeRouting, and groupEdges to bus-group connections); 'organic'; 'circular'
     * (set bundleEdges on dense graphs); 'orthogonal'; 'tree'; 'radial' (set
     * bundleEdges on dense graphs); 'compact-disk'.
     */
    layout: RecipeLayout;
    /** Whether to show a legend mapping colors/shapes to component types. */
    showLegend: boolean;
};
/**
 * The recipe the assistant produces. Coloring is deliberately not part of it —
 * component styling and conditional formatting (perspectives) own that concern,
 * and the diagram resolves colors through the same functions every other view
 * uses. The assistant is only prompted about layout, title, and legend.
 */
export type AssistantDiagramRecipePayload = AssistantDiagramRecipe;
export const DEFAULT_RECIPE: AssistantDiagramRecipe = {
    title: 'Diagram',
    layout: {
        type: 'hierarchical',
        orientation: 'left-to-right',
        edgeRouting: 'orthogonal',
        groupEdges: true,
    },
    showLegend: true,
};
/** Read a string enum field, tolerating `"x"` or `{ kind: "x" }`, else fallback. */
const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T => {
    const raw = typeof value === 'string'
        ? value
        : isRecord(value) && typeof value.kind === 'string'
            ? value.kind
            : undefined;
    return allowed.includes(raw as T) ? (raw as T) : fallback;
};
const LAYOUT_TYPES = [
    'hierarchical',
    'organic',
    'circular',
    'orthogonal',
    'tree',
    'radial',
    'compact-disk',
] as const;
const asBool = (value: unknown, fallback: boolean): boolean => typeof value === 'boolean' ? value : fallback;
/**
 * Resolves the raw `layout` into a nested options record plus its discriminator.
 * Tolerates the object form (`{ type, … }`), a bare layout string (`"circular"`),
 * and a JSON-encoded object string (`'{"type":"circular"}'`) — the last because
 * the Bedrock/LiteLLM tool-calling path can emit a nested-object argument as a
 * stringified object rather than a real one.
 */
const decodeLayout = (raw: unknown): {
    nested: Record<string, unknown>;
    typeValue: unknown;
} => {
    if (isRecord(raw))
        return { nested: raw, typeValue: raw.type };
    if (typeof raw === 'string') {
        try {
            const parsed: unknown = JSON.parse(raw);
            if (isRecord(parsed))
                return { nested: parsed, typeValue: parsed.type };
        }
        catch {
            // Not JSON — fall through and treat the string as a bare layout type.
        }
        return { nested: {}, typeValue: raw };
    }
    return { nested: {}, typeValue: undefined };
};
/**
 * Normalizes the layout into the tagged union, filling each layout's option
 * defaults, and reads layout options whether they're nested under `layout` or
 * sit as top-level siblings.
 */
const parseLayout = (json: Record<string, unknown>): RecipeLayout => {
    const { nested, typeValue } = decodeLayout(json.layout);
    const type = oneOf(typeValue, LAYOUT_TYPES, 'hierarchical');
    // Prefer the option nested under `layout`, falling back to a top-level sibling.
    const opt = (key: string): unknown => nested[key] !== undefined ? nested[key] : json[key];
    switch (type) {
        case 'hierarchical':
            return {
                type,
                orientation: oneOf(opt('orientation'), ['left-to-right', 'top-to-bottom'], 'left-to-right'),
                edgeRouting: oneOf(opt('edgeRouting'), ['orthogonal', 'octilinear', 'polyline', 'curved'], 'orthogonal'),
                groupEdges: asBool(opt('groupEdges'), true),
            };
        case 'circular':
            return { type, bundleEdges: asBool(opt('bundleEdges'), false) };
        case 'radial':
            return { type, bundleEdges: asBool(opt('bundleEdges'), false) };
        case 'organic':
            return { type };
        case 'orthogonal':
            return { type };
        case 'tree':
            return { type };
        case 'compact-disk':
            return { type };
    }
};
/**
 * Validates/normalizes raw LLM output into a `AssistantDiagramRecipe`,
 * filling defaults for missing/invalid fields. Returns `null` only when the
 * input is not an object at all (so the caller can surface a clean error).
 */
export const parseRecipe = (json: unknown): AssistantDiagramRecipe | null => {
    if (!isRecord(json))
        return null;
    return {
        title: typeof json.title === 'string' && json.title.trim().length > 0
            ? json.title.trim()
            : DEFAULT_RECIPE.title,
        layout: parseLayout(json),
        showLegend: typeof json.showLegend === 'boolean'
            ? json.showLegend
            : DEFAULT_RECIPE.showLegend,
    };
};
